// The modules of PRAYOG-QP as the web app shows them: Home's cards, the left menu, each module's home page
// (/m/<slug>) and its illustrative projects (/m/<slug>/examples). Display names only: the server's product keys
// ("skfitter", "transport", ...) and its job kinds stay as they are.
//
// Colours follow the rainbow in module order: DFT violet, Tight Binding indigo, Properties blue, NEGF green, (yellow kept
// for the next module), Spin-Valley Qubits orange, QTetris red.

export type ModuleSlug = "dft" | "tight-binding" | "properties" | "negf" | "spin-valley-qubits" | "qtetris";

export interface SubModule {
  title: string;
  items: string[];
  coming?: boolean;            // shown as "coming", greyed
}

export interface WorksWith {
  dir: "in" | "out";           // "in": where the module's inputs come from; "out": where its results go
  label: string;
  slug?: ModuleSlug;           // a link to that module's home
  note?: string;
}

export interface ModuleInfo {
  slug: ModuleSlug;
  product: string | null;      // the server product (projects, plan limits); null: QTetris, a game without projects
  examplesProduct?: string;    // the illustrative projects' product (GET /examples ... product); none: no gallery yet
  name: string;
  subtitle: string;
  color: string;               // the module's colour (menu dot, card edge, titles)
  tint: string;                // its light background (badges)
  icon: string;                // a symbol in the module header
  group: "pipeline" | "devices";
  question: string;            // the one-line question on Home's card
  tags: string[];
  coming?: string[];           // on Home's card, greyed with a dashed edge
  about: string;               // "What you can do here"
  questions: string[];         // "Questions you can answer"
  worksWith: WorksWith[];
  subs?: SubModule[];          // sub-modules (Properties: its families, from the server's registry instead)
  subsTitle?: string;
  subProducts?: { product: string; name: string; short: string }[];   // sub-modules with projects of their own (Wannier)
  start?: { label: string; to: string };   // instead of "New project" (QTetris: play)
}

export const MODULES: ModuleInfo[] = [
  {
    slug: "dft", product: "dft", examplesProduct: "dft", name: "DFT", subtitle: "Quantum ESPRESSO",
    color: "#862e9c", tint: "#f8f0fc", icon: "ψ", group: "pipeline",
    question: "What are the bands of my material from first principles?",
    tags: ["SCF", "Bands", "DOS", "Projections", "Spin-orbit", "Magnetic", "Recipes checked before they run"],
    about: "Run Quantum ESPRESSO on the server: choose a structure and a ready-made recipe (or your own settings), see what it will cost before it runs, and get the band structure, the density of states and the orbital projections. Spin-orbit coupling, magnetism and DFT+U are covered by the recipes. The results can go straight on to a tight-binding fit.",
    questions: [
      "Is my material a metal, a semiconductor or an insulator, and what is its gap?",
      "Which orbitals make up the bands near the Fermi level?",
      "What do spin-orbit coupling or a magnetic order do to the bands?",
      "Which DFT bands should a tight-binding model reproduce?",
    ],
    worksWith: [
      { dir: "in", label: "Structures", note: "from the library or your own file" },
      { dir: "out", label: "Tight Binding", slug: "tight-binding", note: "Use in Slater-Koster fit" },
    ],
    subsTitle: "What it runs",
    subs: [
      { title: "Ground state", items: ["SCF", "Total energy", "Magnetic moments"] },
      { title: "Bands and DOS", items: ["Band structure", "Density of states", "Orbital projections"] },
      { title: "Spin and correlation", items: ["Spin-orbit coupling", "Collinear and non-collinear magnetism", "DFT+U"] },
    ],
  },
  {
    slug: "tight-binding", product: "skfitter", examplesProduct: "skfitter", name: "Tight Binding", subtitle: "Slater-Koster",
    color: "#4c3fd6", tint: "#efedff", icon: "Ĥ", group: "pipeline",
    question: "Which tight-binding model reproduces my DFT bands?",
    tags: ["VASP / QE input", "Slater-Koster fit", "Wannier functions", "Spin-orbit", "Magnetism", "Overlaps"],
    about: "Fit a Slater-Koster tight-binding model to DFT band structures from VASP or Quantum ESPRESSO: choose the orbitals of each element, the bands and the neighbour shells, start from a published parameter set, and fit with spin-orbit coupling, magnetism and overlaps. Or build a model of maximally localised Wannier functions with Wannier90, from a DFT run of yours or your own interface files. Both models go on to Properties and NEGF Transport.",
    questions: [
      "Which orbitals and neighbour shells does a faithful model of my material need?",
      "How large are its hopping integrals, its spin-orbit coupling and its exchange splitting?",
      "How well does a compact model reproduce the DFT bands near the Fermi level?",
      "Which model should I take on to properties or to a device?",
      "What do the Wannier functions of my bands look like - their centres, spreads and how far their hoppings reach?",
    ],
    worksWith: [
      { dir: "in", label: "DFT", slug: "dft", note: "Quantum ESPRESSO runs, or your VASP / QE files" },
      { dir: "out", label: "Properties", slug: "properties", note: "Continue in Properties" },
      { dir: "out", label: "NEGF Transport", slug: "negf", note: "the fit as a device model" },
      { dir: "out", label: "Wannier90 / WannierBerri", note: "export" },
    ],
    subsTitle: "Sub-modules",
    subs: [
      { title: "Slater-Koster", items: ["DFT data", "Fit set-up", "Fit result", "Export"] },
      { title: "Wannier functions", items: ["From a DFT run", "Your interface files", "Disentanglement", "Quality report"] },
    ],
    subProducts: [{ product: "wannier", name: "Wannier functions", short: "Wannier" }],
  },
  {
    slug: "properties", product: "properties", examplesProduct: "properties", name: "Properties", subtitle: "Berry phase, bands, DOS",
    color: "#1c7ed6", tint: "#e7f5ff", icon: "Ω", group: "pipeline",
    question: "Is it topological? How large is its Hall response?",
    tags: ["Bands & DOS", "Berry curvature", "Chern", "AHC / SHC"],
    about: "Load a model — your Slater-Koster fit, Wannier90 files, a textbook model or a model file — change it if you like (fields, exchange, Rashba, a mass, a scissor) and compute its properties: bands with orbital weights, density of states, band edges and effective masses, Berry curvature, Wilson loops, Chern numbers, anomalous and spin Hall conductivities. Results come with the formula, units and conventions used.",
    questions: [
      "Is my material a Chern or a quantum spin Hall insulator, and what are its topological invariants?",
      "How large is its anomalous or spin Hall conductivity, and how does it change with the Fermi level or temperature?",
      "What do a magnetic exchange field or Rashba coupling from a substrate do to graphene?",
      "Where is the Berry curvature concentrated — which valley, which band?",
      "What are the effective masses at the band edges?",
    ],
    worksWith: [
      { dir: "in", label: "Tight Binding", slug: "tight-binding", note: "fits (Continue in Properties)" },
      { dir: "in", label: "Wannier90", note: "_hr.dat / _tb.dat" },
      { dir: "in", label: "Textbook models", note: "Haldane, Kane-Mele, …" },
      { dir: "in", label: "A model file", note: "prayog_tb JSON" },
      { dir: "out", label: "NEGF Transport", slug: "negf", note: "the same model in a device" },
    ],
    subsTitle: "Families",
  },
  {
    slug: "negf", product: "negf", examplesProduct: "negf", name: "NEGF Transport", subtitle: "devices, Kwant",
    color: "#2f9e44", tint: "#ebfbee", icon: "I", group: "pipeline",
    question: "How does current flow through a nanoscale device?",
    tags: ["Transmission", "I–V", "LDOS", "Gates"],
    about: "Build a device from a tight-binding model — a ribbon, a wire, a junction — add leads and gates, and compute its transmission, current–voltage curves and local density of states with non-equilibrium Green's functions (Kwant). Gates can be solved self-consistently with Poisson's equation; edge magnetism comes from a Hubbard U.",
    questions: [
      "What is the conductance of my nanostructure, and where do its steps come from?",
      "How does the current respond to a bias and to a gate?",
      "Where in the device do the states at a given energy live (edge states, bound states)?",
      "What does spin-orbit coupling or magnetism do to transport?",
    ],
    worksWith: [
      { dir: "in", label: "Tight Binding", slug: "tight-binding", note: "Slater-Koster fits" },
      { dir: "in", label: "Wannier90", note: "files" },
      { dir: "in", label: "Properties", slug: "properties", note: "the same model files" },
    ],
    subsTitle: "Steps",
    subs: [
      { title: "Model", items: ["Slater-Koster fit", "Wannier90", "Model file"] },
      { title: "Device", items: ["Cell", "Transport direction", "Size"] },
      { title: "Gates", items: ["Electrostatics", "Self-consistent NEGF–Poisson"] },
      { title: "Calculation", items: ["T(E)", "I–V", "LDOS", "Fields", "Hubbard U"] },
    ],
  },
  {
    slug: "spin-valley-qubits", product: "transport", name: "Spin-Valley Qubits", subtitle: "quantum dots in Si, Ge and 2D",
    color: "#e8590c", tint: "#fff4e6", icon: "◎", group: "devices",
    question: "What are the levels and transport of my spin-valley qubit?",
    tags: ["Si, Ge, 2D dots", "Energy levels", "Master equation"],
    about: "Model quantum dots in silicon, germanium and 2D materials (bilayer graphene, transition-metal dichalcogenides): the spin and valley energy levels against magnetic field, master-equation transport and dI/dV maps, device characterization from band files, and T1 (readout) simulation.",
    questions: [
      "How do the spin and valley levels of my dot split in a magnetic field?",
      "What does a transport measurement (dI/dV map) of the dot look like?",
      "How do spin-orbit coupling and valley splitting set the qubit's energies?",
      "How fast does the qubit relax (T1) during readout?",
    ],
    worksWith: [{ dir: "in", label: "Band files", note: "for device characterization (2D)" }],
    subsTitle: "What it covers",
    subs: [
      { title: "Silicon and germanium dots", items: ["Energy levels vs field", "Master-equation transport"] },
      { title: "2D-material dots", items: ["BLG", "MoS₂ and other TMDCs", "Presets"] },
      { title: "Devices", items: ["Device characterization", "T1 (readout) simulation"] },
    ],
  },
  {
    slug: "qtetris", product: null, name: "QTetris", subtitle: "learn by playing",
    color: "#e03131", tint: "#fff5f5", icon: "▦", group: "devices",
    question: "How do gates and measurement act on a qubit?",
    tags: ["Bloch sphere", "Gates Rx, Ry, Rz", "Measurement", "3 levels"],
    about: "Quantum Tetris: each falling piece is in a superposition of two pieces. Rotate its qubit on the Bloch sphere with the gates, then measure it - or let the quantum line measure it for you. It runs in the browser; Level 1 is on every plan, Levels 2 and 3 on the Advanced plan.",
    questions: [
      "What does a rotation gate do to a qubit's state on the Bloch sphere?",
      "What happens to a superposition when it is measured?",
      "How does a state that keeps evolving change the odds of a measurement?",
    ],
    worksWith: [],
    subsTitle: "Levels",
    subs: [
      { title: "Level 1: The Bloch Sphere", items: ["Superposition", "Gates", "Measurement"] },
      { title: "Level 2: The \"Gate of Fate\"", items: ["A state that evolves"] },
      { title: "Level 3: The Multiverse", items: ["Two boards", "A shrinking quantum line"] },
    ],
    start: { label: "Play QTetris", to: "/qtetris" },
  },
];

const BY_SLUG = Object.fromEntries(MODULES.map((m) => [m.slug, m])) as Record<ModuleSlug, ModuleInfo>;

export function moduleBySlug(slug: string | undefined): ModuleInfo | undefined {
  return slug ? BY_SLUG[slug as ModuleSlug] : undefined;
}

/** The module of a server product ("skfitter" -> Tight Binding). */
export function moduleOfProduct(product: string | undefined): ModuleInfo | undefined {
  return MODULES.find((m) => m.product === product || m.subProducts?.some((s) => s.product === product));
}

/** A sub-module with projects of its own ("wannier" -> Wannier functions of Tight Binding). */
export function subProductOf(product: string | undefined) {
  for (const m of MODULES) {
    const s = m.subProducts?.find((x) => x.product === product);
    if (s) return { module: m, ...s };
  }
  return undefined;
}

/** The server products of a module's projects (its own and its sub-modules'). */
export const moduleProducts = (m: ModuleInfo): string[] => [...(m.product ? [m.product] : []), ...(m.subProducts ?? []).map((s) => s.product)];

/** The display name of a server product (a product without a module: the server's name, else its key). */
export function displayProductName(product: string, products?: { key: string; name: string }[]): string {
  const sub = subProductOf(product);
  if (sub) return `${sub.name} (${sub.module.name})`;
  return moduleOfProduct(product)?.name ?? products?.find((p) => p.key === product)?.name ?? product;
}

export const modulePath = (slug: ModuleSlug) => `/m/${slug}`;

/** A module's illustrative projects, or one of them. */
export const examplesPath = (slug: ModuleSlug, id?: string) => `/m/${slug}/examples${id ? `/${id}` : ""}`;

/** The gallery of an illustrative project's product ("skfitter" -> /m/tight-binding/examples/<id>). */
export function exampleLink(product: string | undefined, id?: string): string {
  const m = MODULES.find((x) => x.examplesProduct === (product ?? "skfitter"));
  return examplesPath(m?.slug ?? "tight-binding", id);
}

/** The pages before the module pages: old address -> module slug (kept as redirects). */
export const OLD_EXAMPLE_ROUTES: Record<string, ModuleSlug> = {
  "/examples": "tight-binding", "/dft-examples": "dft", "/negf-examples": "negf", "/properties-examples": "properties",
};
